import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    error: auth0Error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const retryCount = useRef(0);
  const retryTimer = useRef(null);

  const fetchProfile = useCallback(async (isRetry = false) => {
    if (!isAuthenticated) {
      setUserProfile(null);
      setLoading(false);
      return;
    }
    if (!isRetry) setProfileError(null);
    try {
      const token = await getAccessTokenSilently();
      let res = await fetch(`${window.location.origin}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Profile fetch failed (${res.status})`);
      }
      let data = await res.json();

      // One-shot self-heal: if this email is an owner but the DB record is
      // out-of-sync (not admin / still on free tier), trigger promotion.
      // Idempotent + safe: the endpoint verifies the allowlist server-side.
      if (!data.is_admin || data.tier === "free") {
        try {
          const healRes = await fetch(`${window.location.origin}/api/auth/self-promote`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (healRes.ok) {
            const healed = await healRes.json();
            if (healed.updated) {
              // Re-fetch profile to pick up the promoted values
              res = await fetch(`${window.location.origin}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (res.ok) data = await res.json();
            }
          }
          // 403 from self-promote just means user isn't in allowlist — ignore.
        } catch {
          // Non-fatal; profile already fetched.
        }
      }

      setUserProfile(data);
      setProfileError(null);
      retryCount.current = 0;
    } catch (err) {
      console.error("Profile fetch error:", err);
      // Auto-retry up to 3 times with increasing delay
      if (retryCount.current < 3) {
        retryCount.current += 1;
        const delay = retryCount.current * 1500;
        retryTimer.current = setTimeout(() => fetchProfile(true), delay);
        return; // Don't set error yet, still retrying
      }
      setProfileError(err.message);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (auth0Loading) return;
    retryCount.current = 0;
    fetchProfile();
    return () => { if (retryTimer.current) clearTimeout(retryTimer.current); };
  }, [auth0Loading, isAuthenticated, fetchProfile]);

  // Auth-enabled fetch helper
  const authFetch = useCallback(
    async (url, options = {}) => {
      try {
        const token = await getAccessTokenSilently();
        const headers = {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        };
        // Don't send Content-Type for FormData (let browser set boundary)
        if (options.body instanceof FormData) {
          delete headers["Content-Type"];
        }
        return fetch(url, { ...options, headers });
      } catch {
        return fetch(url, options);
      }
    },
    [getAccessTokenSilently]
  );

  const login = (opts = {}) => loginWithRedirect(opts);
  const signup = () => loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
  const logout = () => auth0Logout({ logoutParams: { returnTo: window.location.origin } });

  return (
    <AuthContext.Provider
      value={{
        user: userProfile,
        auth0User,
        isAuthenticated,
        loading: auth0Loading || loading,
        profileError: profileError || (auth0Error ? auth0Error.message : null),
        login,
        signup,
        logout,
        authFetch,
        refreshProfile: fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
